import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customSetsApi } from '../api/custom-sets.api';
import type { CreateCustomSetDto, UpdateCustomSetDto } from '../types';

export const useCustomSets = () => {
  return useQuery({
    queryKey: ['customSets'],
    queryFn: () => customSetsApi.getAll(),
  });
};

export const useCustomSet = (id: number) => {
  return useQuery({
    queryKey: ['customSet', id],
    queryFn: () => customSetsApi.getOne(id),
    enabled: !!id,
  });
};

export const useCustomSetProducts = (id: number) => {
  return useQuery({
    queryKey: ['customSetProducts', id],
    queryFn: () => customSetsApi.getProducts(id),
    enabled: !!id,
  });
};

export const useCreateCustomSet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, file }: { data: CreateCustomSetDto; file?: File }) =>
      customSetsApi.create(data, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customSets'] });
    },
  });
};

export const useUpdateCustomSet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data, file }: { id: number; data: UpdateCustomSetDto; file?: File }) =>
      customSetsApi.update(id, data, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customSets'] });
      queryClient.invalidateQueries({ queryKey: ['customSet', variables.id] });
    },
  });
};

export const useAddProductsToCustomSet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, productIds }: { id: number; productIds: number[] }) =>
      customSetsApi.addProducts(id, productIds),
    onSuccess: (_, variables) => {
      // Инвалидируем все списки продуктов
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['customSets'] });
      queryClient.invalidateQueries({ queryKey: ['customSet', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['customSetProducts', variables.id] });
      // Инвалидируем кэш для каждого добавленного продукта
      variables.productIds.forEach(productId => {
        queryClient.invalidateQueries({ queryKey: ['product', productId] });
      });
    },
  });
};

export const useRemoveProductsFromCustomSet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, productIds }: { id: number; productIds: number[] }) =>
      customSetsApi.removeProducts(id, productIds),
    onSuccess: (_, variables) => {
      // Инвалидируем все списки продуктов
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['customSets'] });
      queryClient.invalidateQueries({ queryKey: ['customSet', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['customSetProducts', variables.id] });
      // Инвалидируем кэш для каждого удаленного продукта
      variables.productIds.forEach(productId => {
        queryClient.invalidateQueries({ queryKey: ['product', productId] });
      });
    },
  });
};

export const useDeleteCustomSet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => customSetsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customSets'] });
      queryClient.invalidateQueries({ queryKey: ['home-tiles'] });
    },
  });
};

